import { TabConfig } from '@/src/homepage/constants/tabConfig';
import { Location } from '@/src/homepage/types';
import { RELEASE_ID, RELEASE_REQUEST_HEADER } from '@/lib/release/identity';

type PersonalizedOptions = {
    recentSearches: string[];
    recentViews: string[];
};

/**
 * Constructs a RequestInit object to fetch promoted events based on location, tab settings, and personalization.
 *
 * @param locationData - Information about the location context, such as online/offline status, placeId, and coordinates.
 * @param tabConfig - The number of promoted slots and dates used to fetch.
 * @param personalizedOptions - User-specific personalization data, like recent searches and views.
 * @returns A RequestInit object configured for a POST request with the necessary body and headers for promoted events retrieval.
 */

export const getPromotedEventsOptions = (
    locationData: Location,
    tabConfig: TabConfig,
    personalizedOptions: PersonalizedOptions,
): RequestInit => {
    const geoPoint =
        locationData.isOnline || !locationData.latitude || !locationData.longitude
            ? undefined
            : {
                  lat: locationData.latitude,
                  lon: locationData.longitude,
                  radius: 50,
              };

    return {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            [RELEASE_REQUEST_HEADER]: RELEASE_ID,
        },
        body: JSON.stringify({
            slots: tabConfig.promotedSlots,
            page: 1,
            interface: 'homepage',
            app: 'web',
            personalized_options: {
                recent_searches: personalizedOptions.recentSearches,
                recent_views: personalizedOptions.recentViews,
            },
            search: {
                dates: tabConfig.eventSearch?.dates || ['current_future'],
                place: {
                    is_online: locationData.isOnline,
                    geo_point: geoPoint,
                    place_id: locationData.placeId,
                },
                client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
        }),
    };
};
